import { Direction, Directionality } from '@angular/cdk/bidi';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ENVIRONMENT_INITIALIZER, inject, LOCALE_ID } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { FuseNavigationService } from '@fuse/components/navigation';
import { TranslocoService, provideTransloco, TRANSLOCO_MISSING_HANDLER } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { CustomTranslocoMissingHandler } from './core/transloco/transloco.missing-handler';
import { LanguagesUtils } from './utils/languages.utils';
import { registerLocaleData } from '@angular/common';
import { Router } from '@angular/router';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NoIndexInterceptor } from './core/interceptors/no-index.interceptor';

import localeEn from '@angular/common/locales/en';
import localeTr from '@angular/common/locales/tr';
import localeZh from '@angular/common/locales/zh';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeJa from '@angular/common/locales/ja';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeRu from '@angular/common/locales/ru';
import localeAr from '@angular/common/locales/ar';
import localeArExtra from '@angular/common/locales/extra/ar';
import localeEl from '@angular/common/locales/el';
import localeTh from '@angular/common/locales/th';
import localeHi from '@angular/common/locales/hi';
import localeRo from '@angular/common/locales/ro';
import localeHe from '@angular/common/locales/he';
import { EstablishmentService } from './modules/establishment/establishment.service';
import { PathEnum } from './enums/pathEnum';
import { errorInterceptor } from './errors/error.interceptor';
import { UUIDUtils } from './utils/uuid.utils';
import { FuseConfigService } from '@fuse/services/config';
import { environment } from 'environments/environment';
import { FingerprintService } from './services/fingerprint.service';
import { CampaignReviewService } from './services/campaign-review.service';
import { bindAnalyticsExclusionAliases, captureAnalyticsExclusionFlags } from './services/menu-analytics.session';
import { UrlUtils } from './utils/url.utils';

const APP_DEFAULT_LANG = 'en';

// Available languages configuration - single source of truth
const AVAILABLE_LANGUAGES = [
    { id: 'en', label: 'English' },
    { id: 'tr', label: 'Turkish' },
    { id: 'zh', label: 'Chinese' },
    { id: 'de', label: 'German' },
    { id: 'es', label: 'Spanish' },
    { id: 'fr', label: 'French' },
    { id: 'ja', label: 'Japanese' },
    { id: 'it', label: 'Italian' },
    { id: 'pt', label: 'Portuguese' },
    { id: 'ru', label: 'Russian' },
    { id: 'ar', label: 'Arabic' },
    { id: 'el', label: 'Greek' },
    { id: 'th', label: 'Thai' },
    { id: 'hi', label: 'Hindi' },
    { id: 'ro', label: 'Romanian' },
    { id: 'he', label: 'Hebrew' }
];

// Locale data of every menu language (AVAILABLE_LANGUAGES): the price pipes of the menu receive the
// active language as locale, and Angular throws NG0701 for a locale it has no data for. The extra
// data only serves the day-period date formats ('b', 'B'), which the menu does not use.
registerLocaleData(localeEn);
registerLocaleData(localeTr);
registerLocaleData(localeZh);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeJa);
registerLocaleData(localeIt);
registerLocaleData(localePt);
registerLocaleData(localeRu);
registerLocaleData(localeAr, 'ar', localeArExtra);
registerLocaleData(localeEl);
registerLocaleData(localeTh);
registerLocaleData(localeHi);
registerLocaleData(localeRo);
registerLocaleData(localeHe);

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: LOCALE_ID, useValue: getLocaleId() },
        provideAnimations(),
        provideHttpClient(withInterceptors([errorInterceptor])),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: AVAILABLE_LANGUAGES,
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        
        // Keep <html lang> in step with the displayed language, at boot and on every switch: screen
        // readers choose their voice from it, browsers their hyphenation and CJK glyph forms. The
        // language codes are BCP 47 tags (Japanese is 'ja'), used as they are.
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => {
                inject(TranslocoService).langChanges$.subscribe((lang) => {
                    document.documentElement.lang = lang;
                });
            },
            multi: true,
        },

        // Register custom missing handler for Transloco
        {
            provide: TRANSLOCO_MISSING_HANDLER,
            useClass: CustomTranslocoMissingHandler
        },
        
        // Lay the page out right to left in Arabic and Hebrew, at boot and on every language switch.
        // The CDK's root Directionality reads `dir` only once, and overlays (dialogs, menus) copy its
        // value onto their host element: it is kept in step so they follow the page.
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => {
                const directionality = inject(Directionality);
                inject(TranslocoService).langChanges$.subscribe((lang) => {
                    const dir: Direction = lang === 'ar' || lang === 'he' ? 'rtl' : 'ltr';
                    document.documentElement.dir = dir;
                    if (directionality.value !== dir) {
                        // `value` is readonly in the typings only
                        (directionality as { value: Direction }).value = dir;
                        directionality.change.emit(dir);
                    }
                });
            },
            multi: true,
        },

        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const establishmentService = inject(EstablishmentService);
                const fuseConfigService = inject(FuseConfigService);
                const router = inject(Router);
                const fingerprintService = inject(FingerprintService);
                const campaignReviewService = inject(CampaignReviewService);

                return async () => {
                    // Get the UUID from the first segment after domain
                    const urlSegments = window.location.pathname.split('/');
                    const identifier = urlSegments[1] || null;

                    // Check if skip parameter is present in URL
                    const urlParams = new URLSearchParams(window.location.search);
                    const skip = urlParams.get('skip') === 'true';
                    const previewToken = urlParams.get('previewToken') || undefined;

                    // Store previewToken in campaign service for later use
                    if (previewToken) {
                        campaignReviewService.previewToken = previewToken;
                    }

                    // Persist the analytics exclusion flags for the whole tab session. `skip` was
                    // read once here and kept nowhere, so it vanished from the address on the
                    // first internal navigation and the restaurateur's own browsing was counted as
                    // customer traffic. Wrapped, synchronous and never awaited: an exception on
                    // this path would leave customers on the splash screen, and no analytics
                    // service is instantiated here. The visit counter is told the same thing below:
                    // a reload without `skip`, in a tab that carried it, must count neither a
                    // session nor a visit.
                    let excludedByTab = false;
                    try {
                        excludedByTab = captureAnalyticsExclusionFlags(identifier, window.location.search);
                    } catch {
                        // Boot path: swallow everything.
                    }

                    // The preview token is now held by the campaign service and the tab session: remove it
                    // from the address, which lets Clarity load (index.html keeps it waiting while the
                    // address holds the token). Synchronous, never throws.
                    UrlUtils.removeQueryParamsFromAddressBar(['previewToken']);

                    // Check if we have a valid identifier
                    if (identifier) {
                        // Generate fingerprint before loading establishment so contestGame data is accurate
                        let fingerprint: string | undefined;
                        try {
                            fingerprint = await fingerprintService.generateFingerprint();
                        } catch {
                            // Fingerprint generation failed, proceed without it
                        }

                        // First fetch establishment data
                        return establishmentService.getEstablishmentByIdentifier(identifier, skip || excludedByTab, fingerprint, previewToken)
                        .then(establishment => {
                            if (!establishment) {
                                router.navigate([PathEnum.ERROR, PathEnum.ERROR_MENU_404]);
                            }

                            // A QR code opens the uuid address and the slug replaces it just below,
                            // so the tab's exclusion must hold under both forms. Never throws.
                            try {
                                if (establishment) {
                                    bindAnalyticsExclusionAliases(establishment.uuid, establishment.slug);
                                }
                            } catch {
                                // Boot path: swallow everything.
                            }

                            // If accessed via UUID but establishment has a slug, replace URL with slug
                            // so QR codes (which use UUID) show a clean URL in the browser
                            if (establishment.slug && identifier === establishment.uuid) {
                                const slugUrl = '/' + establishment.slug + window.location.search;
                                window.history.replaceState(null, '', slugUrl);
                            }

                            // Sort establishment languages by position and map to Transloco format
                            const establishmentLangs = establishment.establishmentLanguages
                                .sort((a, b) => a.position - b.position)
                                .map(lang => ({
                                    id: lang.languageRefCode.toLowerCase(),
                                    label: lang.languageRefNativeLabel
                                }));
            
                            // Update available languages in Transloco
                            translocoService.setAvailableLangs(establishmentLangs);
            
                            // Get default language from establishment
                            const defaultLang = establishment.establishmentLanguages.find(l => l.defaultLanguage);
                            const defaultLangCode = defaultLang ? defaultLang.languageRefCode.toLowerCase() : 'en';
            
                            // Get stored language preference
                            const localStorageLang = LanguagesUtils.getLocalLang();
                            let currentLang: string;
            
                            if (localStorageLang && establishmentLangs.some(l => l.id === localStorageLang)) {
                                currentLang = localStorageLang;
                            } else {
                                // Try browser language
                                const browserLang = navigator.language.split('-')[0];
                                if (establishmentLangs.some(l => l.id === browserLang)) {
                                    currentLang = browserLang;
                                } else {
                                    // Fallback to establishment default language
                                    currentLang = defaultLangCode;
                                }
                            }
            
                            LanguagesUtils.saveLangInLocal(currentLang);
                            translocoService.setActiveLang(currentLang);

                            // Set theme based on establishment darkMode
                            fuseConfigService.config = {
                                scheme: establishment.darkMode ? 'dark' : 'light'
                            };
            
                            return firstValueFrom(translocoService.load(currentLang));
                        })
                        .catch(error => {
                            console.error('Error loading establishment:', error);
                            return firstValueFrom(translocoService.load(APP_DEFAULT_LANG));
                        });
                    
                        
                        
                    } else {
                        let currentLang: string;
                        const browserLang = navigator.language.split('-')[0];

                        if(LanguagesUtils.getLocalLang() != null && typeof LanguagesUtils.getLocalLang() != 'undefined'){
                            currentLang = LanguagesUtils.getLocalLang();
                        } else if(translocoService.getAvailableLangs().some(l => l.id === browserLang)) {
                            currentLang = browserLang;
                        } else {
                            currentLang = APP_DEFAULT_LANG;
                        }

                        translocoService.setActiveLang(currentLang);

                        // Redirect to website url
                        window.location.href = environment.websiteUrl;
                        
                    }              
                };
            },
            multi: true,
        },

        // Fuse
        provideIcons(),
        provideAuth(),
        
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-shevafood',
                themes: [
                    {
                        id: 'theme-shevafood',
                        name: 'ShevaFood',
                    },
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        
        // Initialiser explicitement le service de navigation
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(FuseNavigationService),
            multi: true,
        },

        // Register the NoIndexInterceptor
        // This ensures all HTTP responses include no-index headers
        // Part of our comprehensive strategy to prevent menu content indexing
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NoIndexInterceptor,
            multi: true
        }
    ],
};

function getLocaleId() {

    const localStorageLang = LanguagesUtils.getLocalLang();
    let currentLang: string;

    if(localStorageLang != null && typeof localStorageLang != 'undefined'){
        currentLang = localStorageLang;
    } else {

        currentLang = navigator.language; // Navigator default lang
        
        // Use the same available languages from configuration - single source of truth
        const availableLangCodes = AVAILABLE_LANGUAGES.map(lang => lang.id);

        if (!availableLangCodes.some(lang => currentLang.startsWith(lang))) {
            currentLang = APP_DEFAULT_LANG;
        }

    }
    
    return currentLang;
}
